<template>
  <div
    v-if="!loading"
    class="mt-n2"
  >
    <div class="default-table mt-0">
      <div class="table-actions">
        <b-row align-h="between">
          <b-col
            cols="12"
            sm="6"
            class="d-flex d-sm-block justify-content-end mb-1 mb-sm-0"
          >
            <b-dropdown
              id="job-template-filter-dropdown-1"
              :text="jobTemplateTypeText"
              variant="bg-white"
              class="dropdown-icon-wrapper mr-1"
            >
              <template #button-content>
                <span>{{ jobTemplateTypeText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="type in jobTemplateOptions"
                :key="type.id"
                @click="jobTemplateType = type.id"
              >
                {{ type.value }}
              </b-dropdown-item>
            </b-dropdown>

            <b-dropdown
              v-if="showCategoryFilter"
              id="job-template-filter-dropdown-2"
              :text="jobTemplateCategoryText"
              variant="bg-white"
              class="dropdown-icon-wrapper "
            >
              <template #button-content>
                <span>{{ jobTemplateCategoryText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="category in jobTemplateCategoryOptions"
                :key="category.id"
                @click="jobTemplateCategory = category.id"
              >
                {{ category.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col
            class="d-flex justify-content-end"
            cols="12"
            sm="6"
          >
            <b-input-group class="searchInputGroup">
              <b-input-group-prepend is-text>
                <b-icon-search />
              </b-input-group-prepend>
              <b-form-input
                v-model.trim="searchInput"
                type="search"
                class="searchInput"
                placeholder="Search by template name"
              />
            </b-input-group>
          </b-col>
        </b-row>
      </div>
      <div class="position-relative">
        <b-table
          ref="table"
          :select-mode="selectMode"
          :items="jobTemplates"
          :fields="fields"
          :class="{ loaded: !fetchingJobTemplates }"
          show-empty
          selectable
          responsive
          table-class="mb-0"
          @row-clicked="onRowClicked"
        >
          <template #cell(selected)="{ item,rowSelected }">
            <select-button
              :selected="
                rowSelected
                  ? rowSelected
                  : selected
                    ? selected.id === item.id
                    : false
              "
            />
          </template>

          <template #cell(id)="data">
            <a
              class="clickable"
              @click="idClick(data.value)"
            >{{
              data.value
            }}</a>
          </template>

          <template #cell(name)="data">
            <router-link
              :to="{ name: 'job-templates-view', params: { id: data.item.id } }"
              target="_blank"
            >
              {{ data.value }}
            </router-link>
          </template>

          <template #cell(description)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(category)="data">
            {{ data.value || '-' }}
          </template>

          <template #empty>
            <template v-if="fetchingJobTemplates">
              <div class="loading-table text-center py-3">
                <b-spinner variant="primary" />
              </div>
            </template>
            <template v-else>
              <no-data
                no-data-text="No job templates have been added yet..."
                create-text="+ Create a job template"
                icon="Section"
                @action="$emit('create')"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="fetchingJobTemplates && jobTemplates.length > 0" />
      </div>

      <div
        v-if="jobTemplates.length > 0"
        class="default-pagination mt-2"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
  <loading-page v-else />
</template>
<script>
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'JobTemplates',
  components: {
    BIconSearch,
  },
  props: {
    selected: {
      type: Object,
      default: null,
    },
    liveEncoder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectMode: 'single',
      jobTemplateType: 1,
      jobTemplateCategory: 0,
      searchInput: '',
      originsIndex: 1,
      cnamesIndex: 1,
      fields: [
        { key: 'selected', label: '', variant: 'select' },
        { key: 'name', sortable: true, variant: 'fill' },
        'description',
        { key: 'category', variant: 'time' },
      ],
      currentPage: 1,
    }
  },
  computed: {
    loading() {
      return this.fetchingJobTemplateFilters || this.fetchingJobFilters
    },
    jobTemplateOptions() {
      return this.$store.state.jobTemplates.jobTemplateFilters
        ?.jobTemplateOptions
    },
    jobTemplateCategoryOptions() {
      return this.$store.state.jobTemplates.jobTemplateFilters
        ?.jobTemplateCategories
    },
    jobTemplateTypeText() {
      return (
        this.jobTemplateOptions.find(jt => jt.id === this.jobTemplateType)
          ?.value || '-'
      )
    },
    jobTemplateCategoryText() {
      return this.jobTemplateCategoryOptions.find(
        jt => jt.id === this.jobTemplateCategory,
      ).value
    },
    showCategoryFilter() {
      return this.jobTemplateType === 0
    },
    jobTemplates() {
      return this.$store.state.jobTemplates.jobTemplates
    },
    resultInfo() {
      return this.$store.state.jobTemplates.jobTemplatesResultInfo
    },
    fetchingJobTemplates() {
      return this.$store.state.jobTemplates.fetchingJobTemplates
    },
    fetchingJobTemplateFilters() {
      return this.$store.state.jobTemplates.fetchingJobTemplateFilters
    },
    fetchingJobFilters() {
      return this.$store.state.jobs.gettingJobFilters
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
  },
  watch: {
    selected(val) {
      if (!val) {
        this.$refs.table.clearSelected()
      }
    },
    currentPage() {
      this.fetchJobTemplates()
    },
    jobTemplateType() {
      this.jobTemplateCategory = 0
      if (this.currentPage === 1) {
        this.fetchJobTemplates()
      } else {
        this.currentPage = 1
      }
    },
    jobTemplateCategory() {
      if (this.currentPage === 1) {
        this.fetchJobTemplates()
      } else {
        this.currentPage = 1
      }
    },
    searchInput() {
      if (this.currentPage === 1) {
        this.fetchJobTemplates()
      } else {
        this.currentPage = 1
      }
    },
  },
  created() {
    this.init()
  },
  mounted() {
    this.isMounted = true
  },
  methods: {
    onRowClicked(item) {
      this.$emit('selected', item)
    },
    fetchJobTemplates() {
      this.$store
        .dispatch('jobTemplates/fetchJobTemplates', {
          page: this.pageIndex,
          category: this.jobTemplateCategory,
          type: this.jobTemplateType,
          search: this.searchInput,
          liveEncoder: this.liveEncoder,
        })
        .catch(() => {
          this.errorToast('Something went wrong.')
        })
    },
    fetchJobTemplateFilters() {
      this.$store
        .dispatch(
          'jobTemplates/fetchJobTemplateFilters',
          this.liveEncoder ? 1 : 0,
        )
        .catch(() => {
          this.errorToast('Something went wrong.')
        })
    },
    init() {
      this.jobTemplateType = this.liveEncoder ? -2 : -1
      this.fetchJobTemplateFilters()
      this.fetchJobTemplates()
    },
  },
}
</script>

<style scoped>
.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}
.table-responsive {
  margin-bottom: 0;
}
</style>
